import { useState } from 'react';
import axios from 'axios';
import { RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import { latestPlanningWeek, planningMonday, planningWeekLabel } from '@/utils/planningMailDates';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from '@/components/ui/alert-dialog';

const REASONS = {
  eligible: 'À notifier', already_notified: 'Déjà transmis au prestataire',
  no_confirmed_interception: 'Pas d’interception confirmée', no_longer_eligible: 'Salarié non éligible',
  recipient_changed: 'Adresse modifiée — vérification requise', duplicate_address: 'Adresse déjà retenue',
  delivery_uncertain: 'Autre envoi incertain — vérification requise',
  week_not_ready: 'Semaine non prête — voir le motif ci-dessus',
};
const RUN_STATUS = { reserved: 'Envoi réservé', running: 'Envoi en cours', completed: 'Rattrapage terminé', review_required: 'Vérification requise — aucune relance automatique' };

export const PlanningEmailRecovery = () => {
  const [week, setWeek] = useState(latestPlanningWeek);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const load = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.get(`${API}/emails/planning/recovery`, { params: { week_start: week }, withCredentials: true });
      setAudit(data);
    } catch (e) { setError(e.response?.data?.detail || 'Impossible de consulter le rattrapage.'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (busy || !confirmed || !audit || audit.week_start !== week) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/emails/planning/recovery/confirm`, {
        confirmed: true, campaign: audit.campaign, review_token: audit.review_token,
      }, { withCredentials: true });
      setConfirmOpen(false); setConfirmed(false);
      const { data } = await axios.get(`${API}/emails/planning/recovery`, { params: { week_start: week }, withCredentials: true });
      setAudit(data);
    } catch (e) { setError(e.response?.data?.detail || 'Résultat à vérifier avant toute nouvelle action.'); setConfirmOpen(false); }
    finally { setBusy(false); }
  };
  const canSend = audit?.week_start === week && audit?.delivery?.real_send_enabled && audit.recovery_allowed && audit.eligible_count > 0 && !audit.run && !busy;
  return <section className="border-y border-slate-200 py-6 space-y-4" data-testid="planning-recovery-section">
    <div className="flex flex-wrap gap-4 items-start justify-between">
      <div>
        <h2 className="text-lg font-semibold" data-testid="planning-recovery-title">Rattrapage · Semaine du {planningWeekLabel(week)}</h2>
        <p className="text-sm text-slate-600 mt-1" data-testid="planning-recovery-schedule">Envois habituels : dimanche à 1 h, heure de Paris.</p>
      </div>
      <Button variant="outline" onClick={load} disabled={busy || !week} data-testid="planning-recovery-audit-button">
        <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />
        {audit ? 'Actualiser le bilan' : 'Vérifier les destinataires'}
      </Button>
    </div>
    <label className="block text-sm max-w-xs" data-testid="planning-recovery-week-label" htmlFor="planning-recovery-week">Semaine à vérifier
      <Input id="planning-recovery-week" type="date" value={week} disabled={busy} className="mt-1 w-full min-w-0" data-testid="planning-recovery-week-input"
        onChange={e => { setWeek(planningMonday(e.target.value)); setAudit(null); setConfirmed(false); setConfirmOpen(false); setError(''); }} />
    </label>
    {error && <p role="alert" className="text-sm text-red-700" data-testid="planning-recovery-error">{error}</p>}
    {audit && <>
      <p className="flex gap-2 items-center text-sm" data-testid="planning-recovery-delivery-status"><ShieldCheck className="h-4 w-4 shrink-0" />{audit.delivery.reason}</p>
      {audit.block_reason && <div role="status" className="border-l-4 border-amber-500 pl-4 text-sm" data-testid="planning-recovery-block-reason">
        {audit.block_reason}
        {(!audit.publication || audit.publication.status !== 'published') && <Link className="block underline mt-1" to="/admin/b2b/planning" data-testid="planning-recovery-planning-link">Consulter la publication du planning</Link>}
      </div>}
      <p className="text-sm text-slate-600" data-testid="planning-recovery-automatic-status">Automatique : {audit.automatic?.run
        ? (RUN_STATUS[audit.automatic.run.status] || audit.automatic.run.status)
        : (audit.automatic?.due ? 'Aucune exécution enregistrée pour cette semaine' : 'Créneau à venir')}</p>
      <div className="flex flex-wrap gap-5 text-sm">
        <p data-testid="planning-recovery-eligible-count">À notifier : <strong>{audit.eligible_count}</strong></p>
        <p data-testid="planning-recovery-excluded-count">Exclus après vérification : <strong>{audit.excluded_count}</strong></p>
      </div>
      {audit.run && <div role="status" className="border-l-4 border-emerald-600 pl-4 text-sm space-y-1" data-testid="planning-recovery-run-status">
        <p data-testid="planning-recovery-run-label">{RUN_STATUS[audit.run.status] || audit.run.status}</p>
        <p data-testid="planning-recovery-sent-count">Transmis au prestataire : {(audit.run.results || []).filter(r => r.status === 'sent').length}</p>
        <p data-testid="planning-recovery-review-count">À vérifier : {(audit.run.results || []).filter(r => !['sent', 'ignored'].includes(r.status)).length}</p>
      </div>}
      <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="planning-recovery-recipients">
        {audit.recipients.length === 0 && <p className="text-sm text-slate-500" data-testid="planning-recovery-empty">Aucun e-mail correspondant dans cet environnement.</p>}
        {audit.recipients.map((r, i) => <div key={r.user_id} className="grid grid-cols-1 md:grid-cols-3 gap-1 md:gap-4 py-3 border-b text-sm break-words" data-testid={`planning-recovery-recipient-${i}`}>
          <span data-testid={`planning-recovery-name-${i}`}>{r.name}<span className="block text-xs text-slate-500" data-testid={`planning-recovery-email-${i}`}>{r.email_masked}</span></span>
          <span data-testid={`planning-recovery-source-${i}`}>{r.source_statuses.join(', ') || 'Aucune tentative'}</span>
          <span className={r.eligible ? 'text-emerald-700' : 'text-slate-600'} data-testid={`planning-recovery-reason-${i}`}>{REASONS[r.reason] || r.reason}</span>
        </div>)}
      </div>
      <div className="flex flex-wrap justify-end">
        <Button disabled={!canSend} onClick={() => { setConfirmed(false); setConfirmOpen(true); }} data-testid="planning-recovery-open-confirmation">
          <Send className="w-4 h-4 mr-2" />Confirmer le rattrapage
        </Button>
      </div>
    </>}
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent className="max-w-lg" data-testid="planning-recovery-confirmation-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="planning-recovery-confirmation-title">Autoriser cet envoi réel unique ?</AlertDialogTitle>
          <AlertDialogDescription data-testid="planning-recovery-confirmation-description">
            {audit?.eligible_count} destinataire(s) vérifié(s), semaine du {planningWeekLabel(audit?.week_start)}. Aucun planning ne sera modifié. Les envois déjà effectués seront de nouveau contrôlés.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="flex gap-3 text-sm items-start" data-testid="planning-recovery-confirm-label">
          <Checkbox checked={confirmed} onCheckedChange={v => setConfirmed(v === true)} data-testid="planning-recovery-confirm-checkbox" />
          J’autorise cet envoi pour la semaine du {planningWeekLabel(audit?.week_start)}.
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="planning-recovery-cancel-button">Annuler</AlertDialogCancel>
          <Button onClick={send} disabled={!confirmed || busy} data-testid="planning-recovery-send-button">{busy ? 'Réservation…' : 'Autoriser l’envoi unique'}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </section>;
};